import { useState, type FormEvent } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import InlineActionText from '../components/InlineActionText'
import type { SiteContext } from '../components/MainLayout'
import { ArrowIcon, UpworkIcon, ViberIcon } from '../components/icons'
import { StaggerGroup } from '../components/motion'
import { PageIntro } from '../components/ui'
import { routeFor } from '../config/routes'
import { siteConfig } from '../config/site'
import { usePageMeta } from '../hooks/usePageMeta'

type FormStatus = 'idle' | 'sending' | 'success' | 'error'

export default function ContactPageV2() {
  const { language, copy } = useOutletContext<SiteContext>()
  const page = copy.contact
  const formCopy = page.form
  const [status, setStatus] = useState<FormStatus>('idle')
  usePageMeta({ language, title: page.eyebrow, description: page.intro })

  const directContacts = [
    siteConfig.contact.email && { label: 'Email', value: siteConfig.contact.email, href: `mailto:${siteConfig.contact.email}` },
    siteConfig.contact.telephone && { label: language === 'el' ? 'Τηλέφωνο' : 'Telephone', value: siteConfig.contact.telephone, href: `tel:${siteConfig.contact.telephone.replace(/\s/g, '')}` },
    siteConfig.contact.viberBusiness && { label: 'Viber', value: page.viber, href: siteConfig.contact.viberBusiness, kind: 'viber', ariaLabel: page.viber },
    siteConfig.contact.whatsapp && { label: 'WhatsApp', value: 'WhatsApp', href: siteConfig.contact.whatsapp },
    siteConfig.contact.instagram && { label: 'Instagram', value: 'Instagram', href: siteConfig.contact.instagram },
    siteConfig.contact.facebook && { label: 'Facebook', value: 'Facebook', href: siteConfig.contact.facebook },
    siteConfig.contact.linkedin && { label: 'LinkedIn', value: 'LinkedIn', href: siteConfig.contact.linkedin },
    siteConfig.contact.upwork && {
      label: copy.common.upworkShort,
      value: copy.common.upworkLabel,
      href: siteConfig.contact.upwork,
      kind: 'upwork',
      ariaLabel: copy.common.upworkLabel,
    },
  ].filter(Boolean) as Array<{ label: string; value: string; href: string; kind?: 'viber' | 'upwork'; ariaLabel?: string }>

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    setStatus('sending')
    try {
      const formData = new FormData(form)
      const body = new URLSearchParams()
      formData.forEach((value, key) => { if (typeof value === 'string') body.append(key, value) })
      const response = await fetch('/', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body })
      if (!response.ok) throw new Error(`Form submission failed: ${response.status}`)
      form.reset()
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  return (
    <>
      <PageIntro eyebrow={page.eyebrow} title={page.title} text={<InlineActionText text={page.intro} language={language} />} />
      <section className="section contact-section"><StaggerGroup className="shell contact-section__grid">
        <aside className="contact-aside">
          <p className="eyebrow">FM / CONTACT</p><h2>{page.detailsTitle}</h2>
          <dl className="contact-details">
            <div><dt>{page.location}</dt><dd>{siteConfig.location[language]}</dd></div>
            <div><dt>{page.remote}</dt><dd>{language === 'el' ? siteConfig.location.remoteEl : siteConfig.location.remoteEn}</dd></div>
          </dl>
          {directContacts.length > 0 && (
            <div className="direct-contacts">
              <h3>{page.direct}</h3>
              {directContacts.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target={item.href.startsWith('http') ? '_blank' : undefined}
                  rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  aria-label={item.ariaLabel}
                >
                  <span className="direct-contacts__label">
                    {item.kind === 'viber' && <ViberIcon className="viber-icon" />}
                    {item.kind === 'upwork' && <UpworkIcon className="upwork-icon" />}
                    <span>{item.label}</span>
                  </span>
                  <strong>{item.value}</strong>
                </a>
              ))}
            </div>
          )}
          <p className="contact-response-note">{language === 'el' ? 'Συνήθως απαντώ μέσα σε 1–2 εργάσιμες ημέρες.' : 'I usually reply within 1–2 business days.'}</p>
        </aside>
        <form className="contact-form" id="contact-form" name="contact" method="POST" action={routeFor(language, 'thank-you')} data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleSubmit}>
          <input type="hidden" name="form-name" value="contact" /><input type="hidden" name="language" value={language} />
          <p className="visually-hidden"><label>Do not fill this out: <input name="bot-field" /></label></p>
          <div className="form-grid">
            <label className="field"><span>{formCopy.name} <small>{formCopy.required}</small></span><input type="text" name="name" autoComplete="name" required /></label>
            <label className="field"><span>{formCopy.email} <small>{formCopy.required}</small></span><input type="email" name="email" autoComplete="email" required /></label>
          </div>
          <div className="form-grid">
            <label className="field"><span>{formCopy.service} <small>{formCopy.required}</small></span><select name="service" defaultValue="" required><option value="" disabled>{formCopy.servicePlaceholder}</option>{formCopy.services.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
            <label className="field"><span>{formCopy.budget}</span><select name="budget" defaultValue=""><option value="" disabled>{formCopy.budgetPlaceholder}</option>{formCopy.budgets.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          </div>
          <label className="field"><span>{formCopy.description} <small>{formCopy.required}</small></span><textarea name="message" rows={7} placeholder={formCopy.descriptionPlaceholder} required /></label>
          <label className="consent-field"><input type="checkbox" name="consent" value="yes" required /><span>{formCopy.consent}</span></label>
          <p className="contact-form__privacy">{formCopy.privacy} <Link to={routeFor(language, 'privacy')}>{formCopy.privacyLink}</Link></p>
          {status === 'success' && <p className="form-message form-message--success" role="status">{formCopy.success}</p>}
          {status === 'error' && <p className="form-message form-message--error" role="alert">{formCopy.error}</p>}
          <button className="button button--solid contact-form__submit" type="submit" disabled={status === 'sending'}><span>{status === 'sending' ? formCopy.sending : formCopy.submit}</span><ArrowIcon className="button__icon" /></button>
        </form>
      </StaggerGroup></section>
    </>
  )
}
